import { useRef, useState } from 'react';
import { trpc } from '../../api/trpc';

interface Props {
  projectId: string;
}

function formatFileSize(bytes: number | bigint): string {
  const b = Number(bytes);
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1] ?? '';
      resolve(base64);
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

export function FilesTab({ projectId }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const files = trpc.projects.listKBFiles.useQuery({ projectId });
  const uploadToS3 = trpc.files.upload.useMutation();
  const uploadToProject = trpc.projects.uploadKBFile.useMutation();
  const removeProjectFile = trpc.projects.removeKBFile.useMutation();
  const utils = trpc.useUtils();

  const handleUpload = async (file: File) => {
    setUploading(true);
    setError(null);
    try {
      const base64 = await readFileAsBase64(file);

      // Upload to file service first
      const fileRecord = await uploadToS3.mutateAsync({
        filename: file.name,
        mimeType: file.type || 'application/octet-stream',
        content: base64,
      });

      // Then register with project (triggers async processing)
      await uploadToProject.mutateAsync({
        projectId,
        fileRecordId: fileRecord.fileId,
        filename: file.name,
        mimeType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
        fileContent: base64,
      });

      void utils.projects.listKBFiles.invalidate({ projectId });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Upload failed';
      setError(msg);
      void err;
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async (fileId: string) => {
    try {
      await removeProjectFile.mutateAsync({ projectId, kbFileId: fileId });
      void utils.projects.listKBFiles.invalidate({ projectId });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Remove failed');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleUpload(file);
            e.target.value = '';
          }}
        />
        <button
          className="btn btn-primary"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? 'Uploading...' : 'Upload File'}
        </button>
      </div>

      {error && (
        <div style={{
          padding: '8px 12px', marginBottom: 12, borderRadius: 'var(--radius)',
          background: 'var(--sev-high-bg)', color: 'var(--sev-high)',
          fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span>{error}</span>
          <button onClick={() => setError(null)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'inherit', fontSize: 16 }}>x</button>
        </div>
      )}

      {files.data?.length === 0 && !uploading && (
        <div className="proj-empty">
          <div className="proj-empty-title">No files yet</div>
          <div className="proj-empty-desc">
            Upload files for Cade to reference in project conversations.
          </div>
        </div>
      )}

      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      {files.data?.map((file: any) => (
        <div key={file.id} className="conv-item" style={{ cursor: 'default' }}>
          <svg className="conv-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
          </svg>
          <span className="conv-text">{file.filename}</span>
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginRight: 8 }}>
            {formatFileSize(file.sizeBytes)}
          </span>
          <span
            className={`participant-role-badge ${file.status === 'ready' ? 'owner' : ''}`}
            style={file.status === 'error' ? { background: 'var(--sev-high-bg)', color: 'var(--sev-high)' } : undefined}
          >
            {file.status}
          </span>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => void handleRemove(file.id)}
            title="Remove file"
            style={{ marginLeft: 8, padding: '2px 6px', fontSize: 11 }}
          >
            Remove
          </button>
        </div>
      ))}
    </div>
  );
}
